import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  LinearProgress,
  makeStyles,
  Radio,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Delete, DeleteOutlined, GetApp } from "@material-ui/icons";
import { Field, useFormikContext } from "formik";
import { RadioGroup } from "formik-material-ui";
import moment from "moment";
import React from "react";
import useAlerts from "../../Base/AlertProvider";
import {
  deleteRegistrationDocument,
  DocumentType,
  downloadRegistrationDocument,
  getRegistrationDocumentsForRegistrationId,
} from "../../Base/Services/RegistrationDocumentService";

export const ID_DOCUMENT_NAME_1 = "Copy of ID #1";
export const ID_DOCUMENT_NAME_2 = "Copy of ID #2";

const useStyles = makeStyles((theme) => ({
  accordionRadio: {
    marginLeft: theme.spacing(0.5),
  },
}));

export default function VerifyIdentityForm({ registrationId }) {
  const classes = useStyles();
  const { addErrorAlert } = useAlerts();
  const formik = useFormikContext();
  const [loading, setLoading] = React.useState(Boolean(registrationId));
  const [copyOfId1Document, setcopyOfId1Document] = React.useState(null);
  const [copyOfId2Document, setcopyOfId2Document] = React.useState(null);

  function changeVerifyIdentityOption(value) {
    formik.setFieldValue("verifyIdentityOption", value);
  }

  // If registrationId provided, then try to load the existing identity document(s)
  React.useEffect(() => {
    if (registrationId) {
      getRegistrationDocumentsForRegistrationId(registrationId, [DocumentType.IDENTIFICATION])
        .then((identityDocs) => {
          const existingcopyOfId1Doc = identityDocs.find((doc) => doc.documentName === ID_DOCUMENT_NAME_1);
          const existingcopyOfId2Doc = identityDocs.find((doc) => doc.documentName === ID_DOCUMENT_NAME_2);

          if (existingcopyOfId1Doc) {
            setcopyOfId1Document(existingcopyOfId1Doc);
            formik.setFieldValue("copyOfId1", existingcopyOfId1Doc);
          }
          if (existingcopyOfId2Doc) {
            setcopyOfId2Document(existingcopyOfId2Doc);
            formik.setFieldValue("copyOfId2", existingcopyOfId2Doc);
          }
        })
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Typography variant="body1" gutterBottom>
        To verify your identity, please share a copy of your ID from our{" "}
        <a href="https://vitalrecords.utah.gov/wp-content/uploads/ID-list.pdf" target="_blank" rel="noreferrer">
          List of Accepted Identification
        </a>
        . For some types of ID from the list, you may be required to provide 2 different types of ID.
      </Typography>
      <FormControl
        component="fieldset"
        name="verifyIdentityOption"
        style={{ width: "100%" }}
        error={Boolean(formik.touched.verifyIdentityOption && formik.errors.verifyIdentityOption)}
      >
        <FormLabel component="legend">Please choose one:</FormLabel>
        <Field
          component={RadioGroup}
          name="verifyIdentityOption"
          style={{ marginTop: 12, marginBottom: 12 }}
          onChange={(e) => changeVerifyIdentityOption(e.target.value)}
        >
          <Accordion expanded={formik.values.verifyIdentityOption === "upload"} style={{ width: "100%" }}>
            <AccordionSummary
              expandIcon={null}
              arial-label="Select and Expand"
              aria-controls="upload-identity-selection"
              id="upload-identity-selection"
              onClick={() => changeVerifyIdentityOption("upload")}
            >
              <FormControlLabel
                className={classes.accordionRadio}
                value="upload"
                control={<Radio inputProps={{ tabIndex: -1 }} />}
                label="I will upload a copy of my ID now"
              />
            </AccordionSummary>
            <AccordionDetails style={{ display: "block" }}>
              <input
                type="file"
                accept="image/*,.pdf"
                hidden
                name="copyOfId1"
                id="copyOfId1"
                onChange={(e) => {
                  const file = e.currentTarget.files ? e.currentTarget.files[0] : null;
                  if (file?.size > 5000000) {
                    addErrorAlert("File too large. 5MB max");
                    document.getElementById("copyOfId1").value = "";
                    return;
                  }
                  setcopyOfId1Document(file);
                  formik.setFieldValue("copyOfId1", file);
                }}
              />
              <input
                type="file"
                accept="image/*,.pdf"
                hidden
                name="copyOfId2"
                id="copyOfId2"
                onChange={(e) => {
                  const file = e.currentTarget.files ? e.currentTarget.files[0] : null;
                  if (file?.size > 5000000) {
                    addErrorAlert("File too large. 5MB max");
                    document.getElementById("copyOfId2").value = "";
                    return;
                  }
                  setcopyOfId2Document(file);
                  formik.setFieldValue("copyOfId2", file);
                }}
              />
              <input type="file" hidden name="copyOfId2" id="copyOfId2" />
              {formik.errors.identificationDocuments && formik.touched.identificationDocuments && (
                <FormHelperText error={true}>{formik.errors.identificationDocuments}</FormHelperText>
              )}
              <div style={{ maxWidth: 400, marginLeft: "auto", marginRight: "auto" }}>
                {[
                  {
                    documentName: ID_DOCUMENT_NAME_1,
                    document: copyOfId1Document,
                  },
                  {
                    documentName: ID_DOCUMENT_NAME_2,
                    document: copyOfId2Document,
                  },
                ].map((documentInfo, i) => (
                  <Grid container key={i} style={{ marginBottom: 16, marginTop: 16 }}>
                    <Grid item xs={12} sm={8}>
                      <Typography variant="body1" style={{ fontWeight: 500 }}>
                        {documentInfo.documentName}{" "}
                        {documentInfo.documentName === ID_DOCUMENT_NAME_2 ? <em>(if needed)</em> : null}
                      </Typography>
                      <Typography variant="body2">
                        {documentInfo.document?.name ||
                          (documentInfo.document?.uploadedDate
                            ? "Uploaded on " + moment(documentInfo.document.uploadedDate).format("L")
                            : "")}
                      </Typography>
                      {formik.errors[`copyOfId${i + 1}`] && (
                        <FormHelperText error>{formik.errors[`copyOfId${i + 1}`]}</FormHelperText>
                      )}
                      <div>
                        {loading ? (
                          <LinearProgress variant="indeterminate" style={{ width: 50 }} />
                        ) : (
                          <>
                            {documentInfo.document?.registrationDocumentId ? (
                              <>
                                <Tooltip title="Download File">
                                  <IconButton
                                    id={"download-file-btn-" + i}
                                    type="button"
                                    onClick={() => downloadRegistrationDocument(documentInfo.document)}
                                  >
                                    <GetApp />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Remove File">
                                  <IconButton
                                    type="button"
                                    onClick={() => {
                                      if (window.confirm("Are you sure you want to remove this file?")) {
                                        deleteRegistrationDocument(documentInfo.document).then(() => {
                                          if (documentInfo.documentName === ID_DOCUMENT_NAME_1) {
                                            document.getElementById("copyOfId1").value = "";
                                            formik.setFieldValue("copyOfId1", "");
                                            setcopyOfId1Document(null);
                                          } else if (documentInfo.documentName === ID_DOCUMENT_NAME_2) {
                                            document.getElementById("copyOfId2").value = "";
                                            formik.setFieldValue("copyOfId2", "");
                                            setcopyOfId2Document(null);
                                          }
                                        });
                                      }
                                    }}
                                  >
                                    <Delete />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : documentInfo.document ? (
                              <Tooltip title="Remove File">
                                <IconButton
                                  type="button"
                                  id={"remove-file-btn-" + i}
                                  aria-label="remove file"
                                  onClick={() => {
                                    if (documentInfo.documentName === ID_DOCUMENT_NAME_1) {
                                      document.getElementById("copyOfId1").value = "";
                                      formik.setFieldValue("copyOfId1", "");
                                      setcopyOfId1Document(null);
                                    } else if (documentInfo.documentName === ID_DOCUMENT_NAME_2) {
                                      document.getElementById("copyOfId2").value = "";
                                      formik.setFieldValue("copyOfId2", "");
                                      setcopyOfId2Document(null);
                                    }
                                  }}
                                >
                                  <DeleteOutlined />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Button
                                type="button"
                                variant="contained"
                                id={"select-file-btn-" + i}
                                onClick={() => {
                                  if (documentInfo.documentName === ID_DOCUMENT_NAME_1) {
                                    document.getElementById("copyOfId1").click();
                                  } else if (documentInfo.documentName === ID_DOCUMENT_NAME_2) {
                                    document.getElementById("copyOfId2").click();
                                  }
                                }}
                              >
                                Select File
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={4}
                      style={{ maxHeight: 100, maxWidth: 100, marginLeft: "auto", marginRight: "auto" }}
                    >
                      {documentInfo.document?.size && documentInfo.document.type.startsWith("image") && (
                        <img
                          style={{ maxHeight: 100, maxWidth: 100, height: "auto", width: "auto" }}
                          src={URL.createObjectURL(documentInfo.document)}
                          alt={documentInfo.documentName + " preview"}
                        />
                      )}
                    </Grid>
                  </Grid>
                ))}
              </div>
            </AccordionDetails>
          </Accordion>
          <Accordion expanded={formik.values.verifyIdentityOption === "mail"} style={{ width: "100%" }}>
            <AccordionSummary
              expandIcon={null}
              arial-label="Select and Expand"
              aria-controls="mail-identity-selection"
              id="mail-identity-selection"
              onClick={() => changeVerifyIdentityOption("mail")}
            >
              <FormControlLabel
                className={classes.accordionRadio}
                value="mail"
                control={<Radio inputProps={{ tabIndex: -1 }} />}
                label="I will mail a copy of my ID"
              />
            </AccordionSummary>
            <AccordionDetails style={{ display: "block" }}>
              <p>You may mail a copy of your identification to the Office of Vital Records and Statistics:</p>
              <p>
                Vital Records - Utah Adoption Registry
                <br />
                P.O. Box 141012
                <br />
                Salt Lake City, UT 84114
              </p>
            </AccordionDetails>
          </Accordion>
          {formik.touched.verifyIdentityOption && formik.errors.verifyIdentityOption && (
            <FormHelperText>{formik.errors.verifyIdentityOption}</FormHelperText>
          )}
        </Field>
      </FormControl>
    </div>
  );
}
